import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, ButtonGroup, Dropdown, Form, Modal, Tab, Tabs } from "react-bootstrap";
import {
  clamp, FORMATS, LAYOUTS, RARITIES, getRarity, getSceneConfig,
  createCardLayout, measureImage, getImageCrop, constrainCard, scaleCardCluster,
  resolveGlow, getCardFilter, getExportResolution, DEFAULT_CARD_SHADOW, getShadowFilter, getShadowColor,
} from "./ShareCardLayout";
import {
  createTextElement, createImageElement, constrainImageElement, readImageUpload,
  getElementShadow, getTextShadow, DEFAULT_IMAGE_SHADOW, DEFAULT_TEXT_SHADOW,
} from "./ShareCardElements";
import ShareShadowControls from "./ShareShadowControls";
import ShareColorControl from "./ShareColorControl";
import ShareCardLightControls from "./ShareCardLightControls";
import ShareCardLightPreview, { CardLightBackground } from "./ShareCardLightPreview";
import { DEFAULT_CARD_LIGHTING, makeCardLights, paintCardLightShadows, paintLitCard, paintCardLightBackground } from "./ShareCardLighting";
import ShareImageFrameControls from "./ShareImageFrameControls";
import ShareImageFramePreview from "./ShareImageFramePreview";
import { DEFAULT_IMAGE_FRAME, paintImageFrame } from "./ShareImageFrame";
import ShareDecorationPreview from "./ShareDecorationPreview";
import ShareGraphicsPanel from "./ShareGraphicsPanel";
import ShareTooltipPreview from "./ShareTooltipPreview";
import { createTooltipElement, reflowTooltip, loadTooltipAssets, paintTooltipCanvas } from "./ShareTooltip";
import ShareTransformHandles from "./ShareTransformHandles";
import ShareLitCardPreview from "./ShareLitCardPreview";
import { beginPreviewTransform, updatePreviewTransform, getPreviewPoint } from "./SharePreviewTransforms";
import { DECORATIONS, createDecorationElement, paintDecorationCanvas } from "./ShareCardDecorations";
import "./ShareCardEditor.css";

const EXPORT_FORMATS = [
  { id: "png", label: "PNG", mimeType: "image/png", extension: "png", quality: 1 },
  { id: "jpeg", label: "JPEG", mimeType: "image/jpeg", extension: "jpg", quality: 0.95 },
  { id: "webp", label: "WebP", mimeType: "image/webp", extension: "webp", quality: 0.95 },
];

// Add any built-in background image URLs here. Relative URLs are easiest because
// they stay same-origin for PNG export. Cross-origin URLs also work when that
// server allows CORS image fetching.
const BACKGROUND_IMAGE_URLS = [
  "/newsets/images/backgrounds/woodbg2.webp",
  "/newsets/images/backgrounds/blue-playmat.webp",
  "/newsets/images/backgrounds/pile-of-cards.webp",
];

const GRADIENT_BACKGROUNDS = [
  // {
  //   id: "tavern",
  //   name: "Tavern",
  //   css: "radial-gradient(circle at 50% 42%, #7a4722 0%, #3c1d0d 40%, #160b08 100%)",
  //   colors: ["#7a4722", "#3c1d0d", "#160b08"],
  //   kind: "radial",
  // },
  {
    id: "arcane",
    name: "Arcane",
    css: "linear-gradient(135deg, #35115f 0%, #6827a8 48%, #171044 100%)",
    colors: ["#35115f", "#6827a8", "#171044"],
    kind: "linear",
  },
  {
    id: "frost",
    name: "Frost",
    css: "linear-gradient(135deg, #09203f 0%, #16697a 48%, #53b8c7 100%)",
    colors: ["#09203f", "#16697a", "#53b8c7"],
    kind: "linear",
  },
  {
    id: "ember",
    name: "Ember",
    css: "linear-gradient(135deg, #2b0b08 0%, #8a241c 48%, #df7b24 100%)",
    colors: ["#2b0b08", "#8a241c", "#df7b24"],
    kind: "linear",
  },
  {
    id: "midnight",
    name: "Midnight",
    css: "linear-gradient(145deg, #090b16 0%, #111a32 50%, #05060b 100%)",
    colors: ["#090b16", "#111a32", "#05060b"],
    kind: "linear",
  },
];

const IMAGE_BACKGROUNDS = BACKGROUND_IMAGE_URLS.map((src, index) => ({
  id: `image-${index}`,
  name: `Image ${index + 1}`,
  src,
  kind: "image",
}));

const BACKGROUNDS = [
  ...GRADIENT_BACKGROUNDS,
  ...IMAGE_BACKGROUNDS,
  { id: "transparent", name: "Transparent", kind: "transparent" },
];

const getElementDisplayLabel = (element) => {
  if (element.graphic === "tooltip") return `Tooltip: ${element.title || "Untitled"}`;
  if (element.kind === "text") {
    const snippet = (element.text || "Text")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 22);
    return snippet ? `${element.label}: ${snippet}` : element.label;
  }

  return element.label;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to load ${src}`));
    image.src = src;
  });

const getCardProxyUrl = (src) => {
  try {
    const url = new URL(src, window.location.origin);
    if (!url.pathname.startsWith("/cards/")) return null;

    const filename = url.pathname.split("/").pop();
    if (!filename) return null;

    return `/api/card/share-editor-image.php?file=${encodeURIComponent(filename)}`;
  } catch {
    return null;
  }
};

const loadOriginCleanImage = async (src) => {
  const proxyUrl = getCardProxyUrl(src);
  const candidates = [proxyUrl, src].filter(Boolean);
  let lastError = null;

  for (const candidate of candidates) {
    try {
      const response = await fetch(candidate, {
        credentials: "same-origin",
        cache: "force-cache",
      });

      if (!response.ok || response.type === "opaque") {
        throw new Error(`Image request failed (${response.status || "opaque"}).`);
      }

      const blob = await response.blob();
      if (!blob.type.startsWith("image/")) {
        throw new Error("The image request did not return an image.");
      }

      const objectUrl = window.URL.createObjectURL(blob);
      try {
        const image = await loadImage(objectUrl);
        return { image, objectUrl };
      } catch (error) {
        window.URL.revokeObjectURL(objectUrl);
        throw error;
      }
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error(`Unable to load ${src}`);
};

const paintVignette = (context, scene, strength) => {
  if (strength <= 0) return;

  const vignette = context.createRadialGradient(
    scene.width / 2,
    scene.height / 2,
    Math.min(scene.width, scene.height) * 0.28,
    scene.width / 2,
    scene.height / 2,
    Math.max(scene.width, scene.height) * 0.74,
  );
  vignette.addColorStop(0, "rgba(0,0,0,0)");
  vignette.addColorStop(0.58, `rgba(0,0,0,${strength * 0.08})`);
  vignette.addColorStop(0.82, `rgba(0,0,0,${strength * 0.42})`);
  vignette.addColorStop(1, `rgba(0,0,0,${strength})`);
  context.fillStyle = vignette;
  context.fillRect(0, 0, scene.width, scene.height);
};

const paintPresetBackground = (context, preset, scene, pixelRatio = 1) => {
  let gradient;

  if (preset.kind === "radial") {
    gradient = context.createRadialGradient(
      scene.width / 2,
      scene.height * 0.42,
      40 * pixelRatio,
      scene.width / 2,
      scene.height * 0.5,
      Math.max(scene.width, scene.height) * 0.7,
    );
  } else {
    gradient = context.createLinearGradient(0, 0, scene.width, scene.height);
  }

  preset.colors.forEach((color, index) => {
    gradient.addColorStop(index / (preset.colors.length - 1), color);
  });

  context.fillStyle = gradient;
  context.fillRect(0, 0, scene.width, scene.height);
};

const drawImageCover = (context, image, scene) => {
  const imageRatio = image.naturalWidth / image.naturalHeight;
  const sceneRatio = scene.width / scene.height;

  let sourceWidth = image.naturalWidth;
  let sourceHeight = image.naturalHeight;
  let sourceX = 0;
  let sourceY = 0;

  if (imageRatio > sceneRatio) {
    sourceWidth = image.naturalHeight * sceneRatio;
    sourceX = (image.naturalWidth - sourceWidth) / 2;
  } else {
    sourceHeight = image.naturalWidth / sceneRatio;
    sourceY = (image.naturalHeight - sourceHeight) / 2;
  }

  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    scene.width,
    scene.height,
  );
};

const paintBackgroundImage = (context, image, scene, blur) => {
  if (!blur) {
    drawImageCover(context, image, scene);
    return;
  }

  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = scene.width;
  tempCanvas.height = scene.height;
  const tempContext = tempCanvas.getContext("2d");
  if (!tempContext) {
    drawImageCover(context, image, scene);
    return;
  }

  drawImageCover(tempContext, image, scene);

  const overscan = blur * 2;
  context.save();
  context.filter = `blur(${blur}px)`;
  context.drawImage(
    tempCanvas,
    -overscan,
    -overscan,
    scene.width + overscan * 2,
    scene.height + overscan * 2,
  );
  context.restore();
};

const drawTextElement = (context, element, pixelRatio = 1) => {
  const fontSize = element.fontSize;
  const lineHeight = Math.round(fontSize * 1.08);

  context.save();
  context.translate(element.x, element.y);
  context.rotate((element.rotation * Math.PI) / 180);
  context.textAlign = "center";
  context.textBaseline = "top";
  context.fillStyle = element.color || "#ffffff";
  const shadow = getElementShadow(element);
  context.shadowColor = shadow.enabled ? getShadowColor(shadow) : "rgba(0,0,0,0)";
  context.shadowBlur = shadow.enabled ? shadow.blur * pixelRatio : 0;
  context.shadowOffsetX = shadow.enabled ? shadow.offsetX * pixelRatio : 0;
  context.shadowOffsetY = shadow.enabled ? shadow.offsetY * pixelRatio : 0;
  context.font = `800 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

  const lines = String(element.text || "").split("\n");
  const totalHeight = lineHeight * lines.length;
  const startY = -totalHeight / 2;

  lines.forEach((line, index) => {
    context.fillText(line, 0, startY + index * lineHeight);
  });

  context.restore();
};

const createTitleLayout = (text, scene) => {
  const fontSize = Math.round(Math.min(60, scene.width * 0.04, scene.height * 0.045));
  const creatorSize = Math.round(Math.min(28, scene.width * 0.018, scene.height * 0.024));
  const context = document.createElement("canvas").getContext("2d");
  if (!context) return { lines: [text || "Card"], fontSize, creatorSize };
  context.font = `700 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
  const characters = Array.from(text || "Card");
  const lines = [];
  let line = "";
  const maxWidth = scene.width * 0.91;
  for (let index = 0; index < characters.length; index += 1) {
    const next = line + characters[index];
    if (context.measureText(next).width <= maxWidth || !line) { line = next; continue; }
    if (lines.length === 1) {
      while (line && context.measureText(`${line}…`).width > maxWidth) line = line.slice(0, -1);
      line += "…";
      break;
    }
    lines.push(line.trim());
    line = characters[index].trimStart();
  }
  if (line) lines.push(line.trim());
  return { lines, fontSize, creatorSize };
};

const ShareCardEditor = ({ show, onHide, cardInfo, tokens = [] }) => {
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const customPositionsRef = useRef(false);
  const clusterScaleRef = useRef(1);
  const imageUploadInputRef = useRef(null);
  const uploadEpochRef = useRef(0);
  const sceneRef = useRef(null);

  // A stable key keeps parent rerenders from resetting a composition in progress.
  const assetKey = JSON.stringify([
      {
        id: `main-${cardInfo.cardId2}`,
        src: `/cards/${cardInfo.cardId2}.${cardInfo.cardFormat}`,
        label: cardInfo.cardName || "Main card",
        rarity: getRarity(cardInfo),
      },
      ...tokens.map((token, index) => ({
        id: `token-${token.id}-${index}`,
        src: `/cards/${token.id}.${token.format}`,
        label: token.cardName || token.name || token.class || `Token ${index + 1}`,
        rarity: getRarity(token),
      })),
  ]);
  const assets = useMemo(() => JSON.parse(assetKey), [assetKey]);
  const [removedCardIds, setRemovedCardIds] = useState([]);
  const layoutAssets = useMemo(() => assets.filter((asset) => !removedCardIds.includes(asset.id)), [assets, removedCardIds]);

  const [format, setFormat] = useState("auto");
  const [customSize, setCustomSize] = useState({ width: 1600, height: 1000 });
  const [sizeDraft, setSizeDraft] = useState({ width: "1600", height: "1000" });
  const scene = useMemo(() => getSceneConfig(assets.length, format, customSize), [assets.length, format, customSize]);
  sceneRef.current = scene;
  const titleLayout = useMemo(() => createTitleLayout(cardInfo.cardName, scene), [cardInfo.cardName, scene]);
  const [preset, setPreset] = useState("classic");
  const [settingsTab, setSettingsTab] = useState("layout");
  const settingsPanelRef = useRef(null);
  const [spacing, setSpacing] = useState(1);
  const [clusterScale, setClusterScale] = useState(1);
  const [tilt, setTilt] = useState(1);
  const [alignment, setAlignment] = useState("bottom");
  const [trimPadding, setTrimPadding] = useState(true);
  const [keepInside, setKeepInside] = useState(true);
  const [snapToCenter, setSnapToCenter] = useState(true);
  const [guides, setGuides] = useState({ x: false, y: false });
  const [cardImages, setCardImages] = useState({});
  const [imagesLoading, setImagesLoading] = useState(false);
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [tooltipLoading, setTooltipLoading] = useState(false);
  const [imageWarning, setImageWarning] = useState("");
  const [glow, setGlow] = useState({ mode: "off", color: "#80caff", strength: 0.7, radius: 28 });
  const [cardShadow, setCardShadow] = useState(() => ({ ...DEFAULT_CARD_SHADOW }));
  const [cardLighting, setCardLighting] = useState(() => ({ ...DEFAULT_CARD_LIGHTING }));
  const [cardLights, setCardLights] = useState(() => makeCardLights());
  const [selectedLight, setSelectedLight] = useState(null);
  const [imageFrame, setImageFrame] = useState(() => ({ ...DEFAULT_IMAGE_FRAME }));
  const flatCardShadow = cardLighting.enabled ? { ...cardShadow, enabled: false } : cardShadow;
  const [elements, setElements] = useState(() => createCardLayout(assets, scene));
  const [selectedId, setSelectedId] = useState(assets[0]?.id ?? null);
  const [backgroundId, setBackgroundId] = useState(IMAGE_BACKGROUNDS[0]?.id || GRADIENT_BACKGROUNDS[0].id);
  const [customBackground, setCustomBackground] = useState(null);
  const [showTitle, setShowTitle] = useState(true);
  const [showBrand, setShowBrand] = useState(true);
  const [backgroundBlur, setBackgroundBlur] = useState(0);
  const [backgroundDarkness, setBackgroundDarkness] = useState(0.18);
  const [vignetteStrength, setVignetteStrength] = useState(0.42);
  const [stageViewport, setStageViewport] = useState({ width: 0, height: 0 });
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState("png");
  const [exportError, setExportError] = useState("");
  const selectedExportFormat = EXPORT_FORMATS.find((format) => format.id === exportFormat) || EXPORT_FORMATS[0];

  useEffect(() => {
    uploadEpochRef.current += 1;
    setUploadsLoading(false);
    setTooltipLoading(false);
    if (!show) return;

    customPositionsRef.current = false;
    dragRef.current = null;
    setFormat("auto");
    setPreset("classic");
    setSettingsTab("layout");
    setSpacing(1);
    clusterScaleRef.current = 1;
    setClusterScale(1);
    setTilt(1);
    setAlignment("bottom");
    setGuides({ x: false, y: false });
    setRemovedCardIds([]);
    const nextLayout = createCardLayout(assets, getSceneConfig(assets.length));
    setElements(nextLayout);
    setSelectedId(nextLayout[0]?.id ?? null);
    setExportError("");
    setBackgroundBlur(0);
    setBackgroundDarkness(0.18);
    setVignetteStrength(0.42);
    return () => { uploadEpochRef.current += 1; };
  }, [assets, show]);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    const urls = [];
    setCardImages({});
    setImagesLoading(true);
    setImageWarning("");
    Promise.all(assets.map(async (asset) => {
      try {
        const loaded = await loadOriginCleanImage(asset.src);
        if (cancelled) {
          window.URL.revokeObjectURL(loaded.objectUrl);
          return null;
        }
        urls.push(loaded.objectUrl);
        return [asset.id, { ...measureImage(loaded.image), image: loaded.image, previewSrc: loaded.objectUrl }];
      } catch {
        return null;
      }
    })).then((results) => {
      if (cancelled) return;
      setCardImages(Object.fromEntries(results.filter(Boolean)));
      setImagesLoading(false);
      if (results.some((result) => !result)) {
        setImageWarning("Some card images could not be measured. Their original padding is shown; PNG export will retry loading them.");
      }
    });
    return () => {
      cancelled = true;
      urls.forEach((url) => window.URL.revokeObjectURL(url));
    };
  }, [assets, show]);

  const titleInset = scene.height * 0.035 + titleLayout.lines.length * titleLayout.fontSize * 1.05
    + (cardInfo.creatorName ? titleLayout.creatorSize * 1.45 : 0) + Math.min(scene.width, scene.height) * 0.012;
  useEffect(() => {
    if (!show || customPositionsRef.current) return;
    const next = createCardLayout(layoutAssets, scene, {
      preset, spacing, tilt, alignment, trim: trimPadding, images: cardImages,
      showTitle, showBrand, titleInset,
    });
    const scaled = scaleCardCluster(next, clusterScaleRef.current, scene, cardImages, trimPadding, keepInside);
    clusterScaleRef.current = scaled.factor;
    setClusterScale(scaled.factor);
    setElements((previous) => [
      ...scaled.elements.map((element) => {
        const old = previous.find((item) => item.id === element.id);
        return old ? { ...element, z: old.z, glowMode: old.glowMode, glowColor: old.glowColor, rarityOverride: old.rarityOverride } : element;
      }),
      ...previous.filter((element) => element.kind !== "card"),
    ]);
  }, [layoutAssets, scene, preset, spacing, tilt, alignment, trimPadding, cardImages, show, showTitle, showBrand, titleInset, keepInside]);

  useEffect(() => {
    if (!show || !stageRef.current || typeof ResizeObserver === "undefined") return undefined;

    const updateViewport = () => {
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) return;
      setStageViewport({ width: rect.width, height: rect.height });
    };

    updateViewport();
    const observer = new ResizeObserver(updateViewport);
    observer.observe(stageRef.current);
    window.addEventListener("resize", updateViewport);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateViewport);
    };
  }, [show]);

  useEffect(() => {
    const content = settingsPanelRef.current?.querySelector(".tab-content");
    if (content) content.scrollTop = 0;
  }, [settingsTab, show]);

  const selectedElement = elements.find((element) => element.id === selectedId);
  const selectedBackground =
    BACKGROUNDS.find((background) => background.id === backgroundId) || BACKGROUNDS[0];
  const isTransparentBackground = selectedBackground.kind === "transparent";
  const decorationSurfaces = useMemo(() => elements.filter((element) => element.kind === "card" && cardImages[element.id]?.image).map((element) => {
    const crop = getImageCrop(cardImages[element.id], trimPadding);
    const width = scene.baseCardWidth * element.scale;
    return { ...element, width, height: width * crop.height / crop.width, crop, image: cardImages[element.id].image };
  }), [elements, cardImages, trimPadding, scene.baseCardWidth]);

  const updateElement = (id, updates) => {
    const movesCard = elements.find((element) => element.id === id)?.kind === "card" && ["x", "y", "scale", "rotation"].some((key) => key in updates);
    if (movesCard) {
      customPositionsRef.current = true;
    }
    setElements((previous) =>
      previous.map((element) => {
        if (element.id !== id) return element;
        let next = { ...element, ...updates };
        if (next.graphic === "tooltip" && ["title", "text", "layoutWidth"].some((key) => key in updates)) next = reflowTooltip(next);
        if (keepInside && next.kind === "image") return constrainImageElement(next, scene);
        return movesCard && keepInside && next.kind === "card"
          ? constrainCard(next, scene, cardImages, trimPadding) : next;
      }),
    );
  };

  const selectElement = (id) => {
    setSelectedId(id);
    if (id) setSettingsTab("edit");
  };

  const handleBackgroundClick = (event) => {
    // Settings, selectable artwork, and other controls keep the active selection.
    if (event.target.closest(".share-editor-controls, .share-editor-card, .share-editor-image-item, .share-editor-text-item, button, input, select, textarea, a, [role='button'], [role='menu'], [role='menuitem'], [contenteditable='true']")) return;
    setSelectedId(null);
    dragRef.current = null;
    setGuides({ x: false, y: false });
  };

  const moveSelectedLayer = (toFront) => {
    if (selectedElement?.kind === "card") customPositionsRef.current = true;
    setElements((previous) => {
      const sorted = [...previous].sort((a, b) => a.z - b.z);
      const selected = sorted.find((element) => element.id === selectedId);
      if (!selected) return previous;
      const rest = sorted.filter((element) => element.id !== selectedId);
      return (toFront ? [...rest, selected] : [selected, ...rest])
        .map((element, index) => ({ ...element, z: 10 + index }));
    });
  };

  const handlePointerDown = (event, element) => {
    if (event.button !== 0 || (dragRef.current && dragRef.current.pointerId !== event.pointerId)) return;

    event.preventDefault();
    selectElement(element.id);
    event.currentTarget.setPointerCapture?.(event.pointerId);

    dragRef.current = {
      pointerId: event.pointerId,
      id: element.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: element.x,
      startY: element.y,
    };
  };

  const handleTransformPointerDown = (event, mode, corner, size) => {
    if (event.button !== 0 || !selectedElement || isExporting || !stageRef.current || (dragRef.current && dragRef.current.pointerId !== event.pointerId)) return;
    event.preventDefault();
    event.stopPropagation();
    const point = getPreviewPoint(event, stageRef.current.getBoundingClientRect(), scene);
    dragRef.current = {
      ...beginPreviewTransform(selectedElement, size, point, mode, corner),
      id: selectedElement.id, pointerId: event.pointerId,
    };
    setGuides({ x: false, y: false });
    setSettingsTab("edit");
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleTransformKeyDown = (event, mode, corner, size) => {
    if (!selectedElement || !["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(event.key)) return;
    event.preventDefault();
    event.stopPropagation();
    const positive = mode === "rotate" ? ["ArrowRight", "ArrowDown"].includes(event.key) : ["ArrowRight", "ArrowUp"].includes(event.key);
    const direction = positive ? 1 : -1;
    const center = { x: selectedElement.x, y: selectedElement.y };
    if (mode === "rotate") {
      const radians = direction * (event.shiftKey ? 15 : 1) * Math.PI / 180;
      const drag = beginPreviewTransform(selectedElement, size, { x: center.x + 10, y: center.y }, mode);
      updateElement(selectedElement.id, updatePreviewTransform(drag, { x: center.x + Math.cos(radians) * 10, y: center.y + Math.sin(radians) * 10 }, scene, keepInside));
    } else {
      const drag = beginPreviewTransform(selectedElement, size, center, mode, corner);
      const step = direction * (event.shiftKey ? 0.1 : 0.02);
      updateElement(selectedElement.id, updatePreviewTransform(drag, { x: center.x + drag.diagonal.x * step, y: center.y + drag.diagonal.y * step }, scene, keepInside));
    }
  };

  const handleLightPointerDown = (event, light) => {
    if (event.button !== 0 || isExporting || (dragRef.current && dragRef.current.pointerId !== event.pointerId)) return;
    event.preventDefault();
    event.stopPropagation();
    setSelectedLight(light.id);
    dragRef.current = { mode: "light", id: light.id, pointerId: event.pointerId,
      startClientX: event.clientX, startClientY: event.clientY, startX: light.x, startY: light.y };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleLightKeyDown = (event, light) => {
    const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (!directions[event.key]) return;
    event.preventDefault();
    event.stopPropagation();
    const step = event.shiftKey ? 0.05 : 0.01;
    const [dx, dy] = directions[event.key];
    setCardLights((previous) => previous.map((item) => item.id === light.id
      ? { ...item, x: clamp(item.x + dx * step, -0.5, 1.5), y: clamp(item.y + dy * step, -0.5, 1.5) } : item));
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    const stage = stageRef.current;
    if (!drag || !stage || drag.pointerId !== event.pointerId) return;

    const rect = stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    if (drag.mode === "light") {
      const x = clamp(drag.startX + (event.clientX - drag.startClientX) / rect.width, -0.5, 1.5);
      const y = clamp(drag.startY + (event.clientY - drag.startClientY) / rect.height, -0.5, 1.5);
      setCardLights((previous) => previous.map((light) => light.id === drag.id ? { ...light, x, y } : light));
      return;
    }
    if (drag.mode === "resize" || drag.mode === "rotate") {
      updateElement(drag.id, updatePreviewTransform(drag, getPreviewPoint(event, rect, scene), scene, keepInside, event.shiftKey));
      return;
    }
    const deltaX = ((event.clientX - drag.startClientX) / rect.width) * scene.width;
    const deltaY = ((event.clientY - drag.startClientY) / rect.height) * scene.height;
    let x = drag.startX + deltaX;
    let y = drag.startY + deltaY;
    const threshold = 8 * scene.width / rect.width;
    const snapX = snapToCenter && Math.abs(x - scene.width / 2) < threshold;
    const snapY = snapToCenter && Math.abs(y - scene.height / 2) < threshold;
    if (snapX) x = scene.width / 2;
    if (snapY) y = scene.height / 2;
    setGuides({ x: snapX, y: snapY });
    updateElement(drag.id, { x, y });
  };

  const handlePointerUp = (event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setGuides({ x: false, y: false });
  };

  const handleBackgroundUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setExportError("Please choose an image file for the background.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCustomBackground({
        src: String(reader.result),
        name: file.name,
      });
      setBackgroundId("custom");
      setExportError("");
    };
    reader.onerror = () => {
      setExportError("Could not read that background image.");
    };
    reader.readAsDataURL(file);
  };

  const applyLayout = (changes = {}) => {
    const options = { preset, spacing, tilt, alignment, ...changes };
    customPositionsRef.current = false;
    setPreset(options.preset);
    setSpacing(options.spacing);
    setTilt(options.tilt);
    setAlignment(options.alignment);
    const next = createCardLayout(layoutAssets, scene, {
      ...options, trim: trimPadding, images: cardImages, showTitle, showBrand, titleInset,
    });
    const scaled = scaleCardCluster(next, clusterScaleRef.current, scene, cardImages, trimPadding, keepInside);
    clusterScaleRef.current = scaled.factor;
    setClusterScale(scaled.factor);
    setElements((previous) => [
      ...scaled.elements.map((element) => {
        const old = previous.find((item) => item.id === element.id);
        return old ? { ...element, z: old.z, glowMode: old.glowMode, glowColor: old.glowColor, rarityOverride: old.rarityOverride } : element;
      }),
      ...previous.filter((element) => element.kind !== "card"),
    ]);
  };

  const changeClusterScale = (value) => {
    const scaled = scaleCardCluster(elements, value / clusterScaleRef.current, scene, cardImages, trimPadding, keepInside);
    const applied = clusterScaleRef.current * scaled.factor;
    clusterScaleRef.current = applied;
    setClusterScale(applied);
    setElements(scaled.elements);
  };

  const changeFormat = (nextFormat, dimensions = customSize) => {
    const nextScene = getSceneConfig(assets.length, nextFormat, dimensions);
    const factor = Math.min(nextScene.width / scene.width, nextScene.height / scene.height);
    dragRef.current = null;
    setGuides({ x: false, y: false });
    setElements((previous) => previous.map((element) => {
      const next = { ...element, x: element.x / scene.width * nextScene.width, y: element.y / scene.height * nextScene.height };
      if (element.kind === "text") return { ...next, fontSize: element.fontSize * factor };
      if (element.kind === "image") {
        const resized = { ...next, width: element.width * factor, height: element.height * factor, baseWidth: element.baseWidth * factor };
        return keepInside ? constrainImageElement(resized, nextScene) : resized;
      }
      next.scale = element.scale * scene.baseCardWidth / nextScene.baseCardWidth * factor;
      return keepInside ? constrainCard(next, nextScene, cardImages, trimPadding) : next;
    }));
    setCustomSize(dimensions);
    setFormat(nextFormat);
  };

  const commitCustomSize = () => {
    const dimensions = {
      width: clamp(Math.round(Number(sizeDraft.width) || customSize.width), 320, 4096),
      height: clamp(Math.round(Number(sizeDraft.height) || customSize.height), 320, 4096),
    };
    setSizeDraft({ width: String(dimensions.width), height: String(dimensions.height) });
    changeFormat("custom", dimensions);
  };

  const changeTrimPadding = (enabled) => {
    // Keep the artwork at its existing source-pixel scale when toggling padding.
    setElements((previous) => previous.map((element) => {
      if (element.kind !== "card" || !cardImages[element.id]) return element;
      const oldCrop = getImageCrop(cardImages[element.id], trimPadding);
      const crop = getImageCrop(cardImages[element.id], enabled);
      const factor = crop.width / oldCrop.width;
      const next = { ...element, scale: element.scale * factor, nativeWidthRatio: (element.nativeWidthRatio || 1) * factor };
      return keepInside ? constrainCard(next, scene, cardImages, enabled) : next;
    }));
    setTrimPadding(enabled);
  };

  const nudgeSelected = (dx, dy) => {
    if (!selectedElement) return;
    updateElement(selectedElement.id, {
      x: selectedElement.x + dx,
      y: selectedElement.y + dy,
    });
  };

  const addTextElement = () => {
    const textCount = elements.filter((element) => element.kind === "text").length + 1;
    const topZ = Math.max(...elements.map((element) => element.z), 10);
    const nextText = createTextElement(scene, textCount, topZ);
    setSelectedId(nextText.id);
    setSettingsTab("edit");
    setElements((previous) => {
      return [...previous, nextText];
    });
  };

  const addDecoration = (decorationId) => {
    const topZ = Math.max(...elements.map((element) => element.z), 10);
    const decoration = createDecorationElement(scene, decorationId, topZ, selectedElement, cardImages[selectedId], trimPadding);
    setElements((previous) => [...previous, decoration]);
    selectElement(decoration.id);
  };

  const addTooltip = async () => {
    const epoch = uploadEpochRef.current;
    setTooltipLoading(true);
    setExportError("");
    try {
      await loadTooltipAssets();
      if (epoch !== uploadEpochRef.current) return;
      const next = createTooltipElement(sceneRef.current, 0);
      setElements((previous) => [...previous, { ...next, z: Math.max(...previous.map((element) => element.z), 10) + 1 }]);
      selectElement(next.id);
    } catch (error) {
      if (epoch === uploadEpochRef.current) setExportError(error.message || "Could not add the tooltip.");
    } finally {
      if (epoch === uploadEpochRef.current) setTooltipLoading(false);
    }
  };

  const duplicateGraphic = () => {
    if (!selectedElement?.decoration && !selectedElement?.graphic) return;
    const topZ = Math.max(...elements.map((element) => element.z), 10);
    const next = {
      ...selectedElement, id: `graphic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      x: selectedElement.x + selectedElement.width * 0.5, y: selectedElement.y + selectedElement.height * 0.25, z: topZ + 1,
    };
    setElements((previous) => [...previous, keepInside ? constrainImageElement(next, scene) : next]);
    selectElement(next.id);
  };

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const epoch = uploadEpochRef.current;
    setUploadsLoading(true);
    setExportError("");
    try {
      const results = await Promise.allSettled(files.map(readImageUpload));
      if (epoch !== uploadEpochRef.current) return;
      const uploads = results.filter((result) => result.status === "fulfilled").map((result) => result.value);
      const additions = uploads.map((upload, index) => createImageElement(sceneRef.current, upload, 0, index));
      if (additions.length) {
        setElements((previous) => {
          const topZ = Math.max(...previous.map((element) => element.z), 10);
          return [...previous, ...additions.map((element, index) => ({ ...element, z: topZ + index + 1 }))];
        });
        selectElement(additions[0].id);
      }
      const failed = results.find((result) => result.status === "rejected");
      if (failed) setExportError(failed.reason.message || "Could not open the selected image.");
    } catch {
      if (epoch === uploadEpochRef.current) setExportError("Could not add the selected images.");
    } finally {
      if (epoch === uploadEpochRef.current) setUploadsLoading(false);
    }
  };

  const removeSelectedElement = () => {
    if (!selectedElement || isExporting) return;
    if (selectedElement.kind === "card") {
      // Keep the remaining placements; presets must not re-add removed cards.
      customPositionsRef.current = true;
      setRemovedCardIds((previous) => previous.includes(selectedElement.id) ? previous : [...previous, selectedElement.id]);
    }
    dragRef.current = null;
    setGuides({ x: false, y: false });
    setExportError("");
    setElements((previous) => previous.filter((element) => element.id !== selectedElement.id));
    setSelectedId(elements.find((element) => element.id !== selectedElement.id && element.kind === "card")?.id
      ?? elements.find((element) => element.id !== selectedElement.id)?.id ?? null);
  };

  const handleElementKeyDown = (event) => {
    const directions = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0] };
    if (!directions[event.key]) return;
    event.preventDefault();
    event.stopPropagation();
    const amount = event.shiftKey ? 20 : 5;
    nudgeSelected(directions[event.key][0] * amount, directions[event.key][1] * amount);
  };

  const exportImage = async () => {
    setIsExporting(true);
    setExportError("");

    const objectUrls = [];

    try {
      const loadedCards = await Promise.allSettled(elements.filter((element) => element.kind === "card").map(async (element) => {
        const cached = cardImages[element.id];
        const loaded = cached ? null : await loadOriginCleanImage(element.src);
        if (loaded) objectUrls.push(loaded.objectUrl);
        const image = cached?.image || loaded.image;
        const metrics = cached || measureImage(image);
        return [element.id, {
          ...metrics,
          image,
          exportCrop: getImageCrop(metrics, trimPadding && Boolean(cached)),
        }];
      }));
      const failedCard = loadedCards.find((result) => result.status === "rejected");
      if (failedCard) throw failedCard.reason;
      const preparedImages = Object.fromEntries(loadedCards.map((result) => result.value));
      const tooltipAssets = elements.some((element) => element.graphic === "tooltip") ? await loadTooltipAssets() : null;
      const uploadedImages = Object.fromEntries(await Promise.all(elements.filter((element) => element.kind === "image" && !element.decoration && !element.graphic).map(async (element) => [element.id, await loadImage(element.src)])));
      const exportResolution = getExportResolution(elements, scene, preparedImages, trimPadding, format === "custom" ? 0 : undefined);
      if (!exportResolution.meetsMinimum) {
        throw new Error("This arrangement needs an image larger than the export limit to keep every card readable. Try Gallery or increase the overall card size.");
      }
      const exportScene = {
        ...scene,
        width: exportResolution.width,
        height: exportResolution.height,
        baseCardWidth: scene.baseCardWidth * exportResolution.scale,
      };
      const exportElements = elements.map((element) => ({
        ...element,
        x: element.x * exportResolution.scale,
        y: element.y * exportResolution.scale,
        ...(element.kind === "text" ? { fontSize: element.fontSize * exportResolution.scale } : {}),
        ...(element.kind === "image" ? { width: element.width * exportResolution.scale, height: element.height * exportResolution.scale } : {}),
      }));
      const exportTitleLayout = {
        ...titleLayout,
        fontSize: titleLayout.fontSize * exportResolution.scale,
        creatorSize: titleLayout.creatorSize * exportResolution.scale,
      };
      const exportBlur = backgroundBlur * exportResolution.scale;
      const canvas = document.createElement("canvas");
      canvas.width = exportScene.width;
      canvas.height = exportScene.height;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Canvas is not available in this browser.");

      if (backgroundId === "custom" && customBackground?.src) {
        const backgroundImage = await loadImage(customBackground.src);
        paintBackgroundImage(context, backgroundImage, exportScene, exportBlur);
      } else if (selectedBackground.kind === "image") {
        const loadedBackground = await loadOriginCleanImage(selectedBackground.src);
        objectUrls.push(loadedBackground.objectUrl);
        paintBackgroundImage(context, loadedBackground.image, exportScene, exportBlur);
      } else if (!isTransparentBackground) {
        paintPresetBackground(context, selectedBackground, exportScene, exportResolution.scale);
      }

      if (!isTransparentBackground && backgroundDarkness > 0) {
        context.fillStyle = `rgba(0,0,0,${backgroundDarkness})`;
        context.fillRect(0, 0, exportScene.width, exportScene.height);
      }

      if (!isTransparentBackground && vignetteStrength > 0) {
        paintVignette(context, exportScene, vignetteStrength);
      }

      if (!isTransparentBackground) paintCardLightBackground(context, exportScene, cardLighting, cardLights);

      const orderedElements = [...exportElements].sort((a, b) => a.z - b.z);
      const decorationCanvas = document.createElement("canvas");
      const tooltipCanvas = document.createElement("canvas");
      const litCardCanvas = document.createElement("canvas");
      const exportSurfaces = exportElements.filter((element) => element.kind === "card").map((element) => {
        const { image, exportCrop: crop } = preparedImages[element.id];
        const width = exportScene.baseCardWidth * element.scale;
        return { ...element, width, height: width * crop.height / crop.width, crop, image };
      });

      paintCardLightShadows(context, exportSurfaces, exportScene, cardLighting, cardLights, cardShadow);

      for (const element of orderedElements) {
        if (element.kind === "text") {
          drawTextElement(context, element, exportResolution.scale);
          continue;
        }
        if (element.kind === "image") {
          context.save();
          context.translate(element.x, element.y);
          context.rotate(element.rotation * Math.PI / 180);
          context.filter = getShadowFilter(getElementShadow(element), exportResolution.scale);
          const image = element.graphic === "tooltip"
            ? paintTooltipCanvas(tooltipCanvas, element, tooltipAssets, 2)
            : element.decoration
              ? paintDecorationCanvas(decorationCanvas, element, exportScene, 1, 1, Infinity, exportSurfaces)
              : uploadedImages[element.id];
          context.drawImage(image, -element.width / 2, -element.height / 2, element.width, element.height);
          context.restore();
          continue;
        }

        const { image, exportCrop: crop } = preparedImages[element.id];
        const width = exportScene.baseCardWidth * element.scale;
        const height = width * crop.height / crop.width;

        context.save();
        context.translate(element.x, element.y);
        context.rotate((element.rotation * Math.PI) / 180);
        context.filter = getCardFilter(resolveGlow(element, glow), exportResolution.scale, flatCardShadow);
        if (cardLighting.enabled) {
          const surface = { ...element, width, height, crop, image };
          paintLitCard(litCardCanvas, surface, exportScene, cardLighting, cardLights);
          context.drawImage(litCardCanvas, -width / 2, -height / 2, width, height);
        } else {
          context.drawImage(image, crop.x, crop.y, crop.width, crop.height, -width / 2, -height / 2, width, height);
        }
        context.restore();
      }

      paintImageFrame(context, exportScene, imageFrame);

      if (showTitle) {
        const left = exportScene.width * 0.045;
        const top = exportScene.height * 0.035;
        context.save();
        context.textBaseline = "top";
        context.shadowColor = "rgba(0,0,0,0.7)";
        context.shadowBlur = 18 * exportResolution.scale;
        context.fillStyle = "#ffffff";
        context.font = `700 ${exportTitleLayout.fontSize}px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
        exportTitleLayout.lines.forEach((line, index) => {
          const y = top + index * exportTitleLayout.fontSize * 1.05;
          context.fillText(line, left, y);
        });
        if (cardInfo.creatorName) {
          context.fillStyle = "rgba(255,255,255,0.76)";
          context.font = `500 ${exportTitleLayout.creatorSize}px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
          const creatorY = top + exportTitleLayout.lines.length * exportTitleLayout.fontSize * 1.05 + exportTitleLayout.creatorSize * 0.25;
          context.fillText(`by ${cardInfo.creatorName}`, left, creatorY, exportScene.width * 0.91);
        }
        context.restore();
      }

      if (showBrand) {
        context.save();
        context.textAlign = "right";
        context.shadowColor = "rgba(0,0,0,0.7)";
        context.shadowBlur = 8 * exportResolution.scale;
        context.fillStyle = "rgba(255,255,255,0.76)";
        context.font = `700 ${Math.round(clamp(scene.width * 0.015, 18, 24) * exportResolution.scale)}px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
        const brandX = exportScene.width - exportScene.width * 0.035;
        const brandY = exportScene.height - exportScene.height * 0.045;
        context.fillText("HearthCards.net", brandX, brandY);
        context.restore();
      }

      if (selectedExportFormat.id === "jpeg") {
        context.save();
        context.globalCompositeOperation = "destination-over";
        context.fillStyle = "#10141d";
        context.fillRect(0, 0, exportScene.width, exportScene.height);
        context.restore();
      }

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (nextBlob) => {
            if (!nextBlob) {
              reject(new Error("Image export failed."));
            } else if (nextBlob.type !== selectedExportFormat.mimeType) {
              reject(new Error(`${selectedExportFormat.label} export is not supported by this browser. Choose another format.`));
            } else {
              resolve(nextBlob);
            }
          },
          selectedExportFormat.mimeType,
          selectedExportFormat.quality,
        );
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = (cardInfo.cardName || "card")
        .replace(/[^a-z0-9-_]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
      link.href = url;
      link.download = `${safeName || "card"}-share.${selectedExportFormat.extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting custom share image:", error);
      setExportError(
        error.message ||
          "Could not export the image. Make sure share-editor-image.php is installed on the server.",
      );
    } finally {
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
      setIsExporting(false);
    }
  };

  const pixelRatio = stageViewport.width / scene.width || 0.5;
  const stageStyle = {
    aspectRatio: `${scene.width} / ${scene.height}`,
    width: "100%",
    maxWidth: `min(100%, calc(var(--share-editor-preview-height) * ${scene.width / scene.height}))`,
  };

  const activeBackgroundImage =
    backgroundId === "custom" && customBackground?.src
      ? customBackground.src
      : selectedBackground.kind === "image"
        ? selectedBackground.src
        : null;

  const backgroundLayerStyle = activeBackgroundImage
    ? {
        backgroundImage: `url(${activeBackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        transform: `scale(${1 + backgroundBlur * 4 / Math.min(scene.width, scene.height)})`,
        filter: `blur(${backgroundBlur * pixelRatio}px)`,
      }
    : {
        background: selectedBackground.css,
        transform: "none",
        filter: "none",
      };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="share-editor-modal"
      contentClassName="share-editor-modal-content"
      aria-label="Share image editor"
      onClick={handleBackgroundClick}
    >
      <Modal.Body className="share-editor-body">
        <div className="share-editor-layout">
          <div className="share-editor-workspace-wrap">
            <div
              ref={stageRef}
              className={`share-editor-stage ${isTransparentBackground ? "is-transparent" : ""} ${selectedElement && stageViewport.width ? "has-transform-handles" : ""}`}
              style={stageStyle}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onLostPointerCapture={handlePointerUp}
            >
              {!isTransparentBackground && <>
                <div className="share-editor-background-layer" style={backgroundLayerStyle} />
                <div
                  className="share-editor-darken"
                  style={{ opacity: clamp(backgroundDarkness, 0, 0.75) }}
                />
                <div
                  className="share-editor-vignette"
                  style={{ opacity: clamp(vignetteStrength, 0, 1) }}
                />
                {cardLighting.enabled && <CardLightBackground scene={scene} lighting={cardLighting} lights={cardLights} pixelRatio={pixelRatio} />}
              </>}

              {cardLighting.enabled && cardShadow.enabled && <ShareCardLightPreview surfaces={decorationSurfaces}
                scene={scene} lighting={cardLighting} lights={cardLights} shadow={cardShadow} pixelRatio={pixelRatio} />}

              {showTitle && (
                <div className="share-editor-title" aria-hidden="true" style={{ fontSize: `${titleLayout.fontSize * pixelRatio}px` }}>
                  <div>{titleLayout.lines.map((line, index) => <div key={index}>{line}</div>)}</div>
                  {cardInfo.creatorName && <small style={{ fontSize: `${titleLayout.creatorSize * pixelRatio}px` }}>by {cardInfo.creatorName}</small>}
                </div>
              )}

              {[...elements]
                .sort((a, b) => a.z - b.z)
                .map((element) => {
                  if (element.kind === "text") {
                    const fontSizePx = stageViewport.width
                      ? (element.fontSize / scene.width) * stageViewport.width
                      : 28;

                    return (
                      <button
                        type="button"
                        key={element.id}
                        className={`share-editor-text-item ${
                          selectedId === element.id ? "is-selected" : ""
                        }`}
                        style={{
                          left: `${(element.x / scene.width) * 100}%`,
                          top: `${(element.y / scene.height) * 100}%`,
                          zIndex: element.z,
                          transform: `translate(-50%, -50%) rotate(${element.rotation}deg)`,
                        }}
                        onPointerDown={(event) => handlePointerDown(event, element)}
                        onClick={() => selectElement(element.id)}
                        onFocus={() => selectElement(element.id)}
                        onKeyDown={handleElementKeyDown}
                        aria-label={`Move ${element.label}`}
                      >
                        <div
                          className="share-editor-text-content"
                          style={{
                            fontSize: `${fontSizePx}px`,
                            color: element.color || "#ffffff",
                            textShadow: getTextShadow(element, pixelRatio),
                          }}
                        >{element.text}</div>
                      </button>
                    );
                  }

                  if (element.kind === "image") return (
                    <button
                      type="button" key={element.id}
                      className={`share-editor-image-item ${selectedId === element.id ? "is-selected" : ""}`}
                      style={{
                        left: `${element.x / scene.width * 100}%`, top: `${element.y / scene.height * 100}%`,
                        width: `${element.width / scene.width * 100}%`, height: `${element.height / scene.height * 100}%`,
                        zIndex: element.z, transform: `translate(-50%, -50%) rotate(${element.rotation}deg)`,
                      }}
                      onPointerDown={(event) => handlePointerDown(event, element)}
                      onClick={() => selectElement(element.id)} onFocus={() => selectElement(element.id)}
                      onKeyDown={handleElementKeyDown} aria-label={`Move ${element.label}`}
                    >
                      {element.graphic === "tooltip" ? <ShareTooltipPreview element={element} pixelRatio={pixelRatio} style={{ filter: getShadowFilter(getElementShadow(element), pixelRatio) }} /> : element.decoration ? (
                        <ShareDecorationPreview element={element} scene={scene} pixelRatio={pixelRatio} surfaces={element.decoration.endsWith("-tape") ? decorationSurfaces : undefined} style={{ filter: getShadowFilter(getElementShadow(element), pixelRatio) }} />
                      ) : <img src={element.src} alt={element.label} draggable={false} style={{ filter: getShadowFilter(getElementShadow(element), pixelRatio) }} />}
                    </button>
                  );

                  const metrics = cardImages[element.id];
                  const crop = getImageCrop(metrics, trimPadding);

                  return (
                    <button
                      type="button"
                      key={element.id}
                      className={`share-editor-card ${
                        selectedId === element.id ? "is-selected" : ""
                      }`}
                      style={{
                        left: `${(element.x / scene.width) * 100}%`,
                        top: `${(element.y / scene.height) * 100}%`,
                        width: `${(scene.baseCardWidth * element.scale / scene.width) * 100}%`,
                        zIndex: element.z,
                        transform: `translate(-50%, -50%) rotate(${element.rotation}deg)`,
                      }}
                      onPointerDown={(event) => handlePointerDown(event, element)}
                      onClick={() => selectElement(element.id)}
                      onFocus={() => selectElement(element.id)}
                      onKeyDown={handleElementKeyDown}
                      aria-label={`Move ${element.label}`}
                    >
                      <span className="share-editor-card-effects" style={{ filter: getCardFilter(resolveGlow(element, glow), pixelRatio, flatCardShadow) }}>
                        <span className="share-editor-card-crop" style={metrics ? { aspectRatio: `${crop.width} / ${crop.height}` } : undefined}>
                          {cardLighting.enabled && metrics?.image ? <ShareLitCardPreview
                            surface={decorationSurfaces.find((surface) => surface.id === element.id)}
                            scene={scene} lighting={cardLighting} lights={cardLights} pixelRatio={pixelRatio} /> : (
                            <img src={metrics?.previewSrc || element.src} alt={element.label} draggable={false} style={metrics ? {
                              position: "absolute", width: `${metrics.width / crop.width * 100}%`, height: `${metrics.height / crop.height * 100}%`,
                              maxWidth: "none", left: `${-crop.x / crop.width * 100}%`, top: `${-crop.y / crop.height * 100}%`,
                            } : undefined} />
                          )}
                        </span>
                      </span>
                    </button>
                  );
                })}

              <ShareImageFramePreview scene={scene} frame={imageFrame} />
              {cardLighting.enabled && cardShadow.enabled && settingsTab === "style" && cardLights.map((light, index) => <button
                type="button" key={light.id} className={`share-editor-light-handle ${selectedLight === light.id ? "is-selected" : ""}`}
                style={{ left: `${clamp(light.x * 100, 3, 97)}%`, top: `${clamp(light.y * 100, 4, 96)}%` }}
                aria-label={`Move light ${index + 1}`} title={`Light ${index + 1}`}
                onPointerDown={(event) => handleLightPointerDown(event, light)}
                onKeyDown={(event) => handleLightKeyDown(event, light)} onFocus={() => setSelectedLight(light.id)}
              ><span aria-hidden="true">☼</span><small>{index + 1}</small></button>)}

              {showBrand && (
                <div className="share-editor-brand" aria-hidden="true" style={{ fontSize: `${clamp(scene.width * 0.015, 18, 24) * pixelRatio}px` }}>
                  HearthCards.net
                </div>
              )}
              {guides.x && <div className="share-editor-guide is-vertical" />}
              {guides.y && <div className="share-editor-guide is-horizontal" />}
              {selectedElement && <ShareTransformHandles key={selectedElement.id} element={selectedElement} scene={scene}
                metrics={cardImages[selectedElement.id]} trim={trimPadding} viewport={stageViewport} stageRef={stageRef}
                onPointerDown={handleTransformPointerDown} onKeyDown={handleTransformKeyDown} disabled={isExporting} />}
            </div>
            {imageWarning && <div className="share-editor-readability is-tight" role="status">{imageWarning}</div>}
          </div>

          <aside className="share-editor-controls" ref={settingsPanelRef} aria-label="Image settings">
            <Tabs id="share-editor-settings" activeKey={settingsTab} onSelect={(key) => { if (key) setSettingsTab(key); }} className="share-editor-settings-tabs" transition={false} mountOnEnter>
              <Tab eventKey="layout" title="Layout" className="share-editor-settings-panel is-layout">
                <section>
                  <div className="share-editor-control-title">Canvas</div>
                  <Form.Label htmlFor="share-editor-format" className="share-editor-label">Aspect ratio</Form.Label>
                  <Form.Select id="share-editor-format" size="sm" value={format} onChange={(event) => changeFormat(event.target.value)}>
                    {FORMATS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                  </Form.Select>
                  {format === "custom" && (
                    <div className="share-editor-dimensions">
                      {["width", "height"].map((axis) => (
                        <label key={axis}>{axis === "width" ? "Width" : "Height"}
                          <Form.Control size="sm" type="number" min={320} max={4096} step={1} value={sizeDraft[axis]}
                            onChange={(event) => setSizeDraft((previous) => ({ ...previous, [axis]: event.target.value }))}
                            onBlur={commitCustomSize} onKeyDown={(event) => { if (event.key === "Enter") event.currentTarget.blur(); }} />
                        </label>
                      ))}
                    </div>
                  )}
                </section>
                <section>
                  <div className="share-editor-control-title">Arrangement</div>
                  <div className="share-editor-presets mb-3" aria-label="Card layout presets">
                    {LAYOUTS.map((layout) => (
                      <button type="button" key={layout.id} className={`share-editor-preset ${preset === layout.id ? "is-selected" : ""}`}
                        onClick={() => applyLayout({ preset: layout.id })} aria-pressed={preset === layout.id}>
                        <span className={`share-editor-preset-art is-${layout.id}`} aria-hidden="true"><i /><i /><i /></span>
                        <span>{layout.name}</span>
                      </button>
                    ))}
                  </div>
                  <Form.Label className="share-editor-label">Overall card size <span>{Math.round(clusterScale * 100)}%</span></Form.Label>
                  <Form.Range aria-label="Overall card cluster size" min={0.5} max={2} step={0.01} value={clusterScale} onChange={(event) => changeClusterScale(Number(event.target.value))} />
                  <Form.Label className="share-editor-label">Spacing <span>{spacing < 0 ? `Overlap ${Math.round(-spacing * 100)}%` : `${Math.round(spacing * 100)}%`}</span></Form.Label>
                  <Form.Range aria-label="Layout spacing" min={-0.75} max={1.8} step={0.01} value={spacing} onChange={(event) => applyLayout({ spacing: Number(event.target.value) })} />
                  <Form.Label className="share-editor-label">Playfulness <span>{tilt === 0 ? "Straight" : `${Math.round(tilt * 100)}%`}</span></Form.Label>
                  <Form.Range aria-label="Layout playfulness" min={0} max={1.5} step={0.05} value={tilt} onChange={(event) => applyLayout({ tilt: Number(event.target.value) })} />
                  <details className="share-editor-placement">
                    <summary>Placement options</summary>
                    <Form.Label htmlFor="share-editor-alignment" className="share-editor-label">Row alignment</Form.Label>
                    <Form.Select id="share-editor-alignment" size="sm" className="mb-3" value={alignment} onChange={(event) => applyLayout({ alignment: event.target.value })}>
                      <option value="bottom">Visible bottoms</option><option value="center">Centers</option>
                    </Form.Select>
                    <Form.Check type="switch" id="share-editor-trim" label="Ignore transparent padding" checked={trimPadding} onChange={(event) => changeTrimPadding(event.target.checked)} />
                    <Form.Check type="switch" id="share-editor-inside" label="Keep artwork inside canvas" checked={keepInside} onChange={(event) => {
                      setKeepInside(event.target.checked);
                      if (event.target.checked) {
                        const scaled = scaleCardCluster(elements, 1, scene, cardImages, trimPadding, true);
                        clusterScaleRef.current *= scaled.factor;
                        setClusterScale(clusterScaleRef.current);
                        setElements(scaled.elements.map((element) => element.kind === "image" ? constrainImageElement(element, scene) : element));
                      }
                    }} />
                    <Form.Check type="switch" id="share-editor-snap" label="Snap to canvas center" checked={snapToCenter} onChange={(event) => setSnapToCenter(event.target.checked)} />
                  </details>
                  <Button variant="outline-light" size="sm" className="w-100 mt-3" onClick={() => applyLayout()}>Reapply layout</Button>
                </section>
              </Tab>
              <Tab eventKey="style" title="Style" className="share-editor-settings-panel is-style">
                <section>
                  <div className="share-editor-control-title">Background</div>
                  <div className="share-editor-backgrounds">
                    {BACKGROUNDS.map((background) => (
                      <button
                        type="button"
                        key={background.id}
                        className={`share-editor-background-swatch ${background.kind === "transparent" ? "is-transparent" : ""} ${
                          backgroundId === background.id ? "is-selected" : ""
                        }`}
                        style={
                          background.kind === "transparent" ? undefined : background.kind === "image"
                            ? {
                                backgroundImage: `url(${background.src})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                              }
                            : { background: background.css }
                        }
                        onClick={() => setBackgroundId(background.id)}
                        title={background.name}
                        aria-label={`Use ${background.name} background`}
                      />
                    ))}
                    {customBackground && (
                      <button
                        type="button"
                        className={`share-editor-background-swatch share-editor-background-custom ${
                          backgroundId === "custom" ? "is-selected" : ""
                        }`}
                        style={{
                          backgroundImage: `url(${customBackground.src})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                        onClick={() => setBackgroundId("custom")}
                        title={customBackground.name}
                        aria-label="Use uploaded background"
                      />
                    )}
                  </div>
                  <Form.Control
                    className="mt-2"
                    size="sm"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleBackgroundUpload}
                    aria-label="Upload custom background"
                  />

                  {!isTransparentBackground && <>
                    <Form.Label className="share-editor-label mt-3">
                      Blur <span>{Math.round(backgroundBlur)}px</span>
                    </Form.Label>
                    <Form.Range
                      aria-label="Background image blur"
                      disabled={!activeBackgroundImage}
                      min={0}
                      max={24}
                      step={1}
                      value={backgroundBlur}
                      onChange={(event) => setBackgroundBlur(Number(event.target.value))}
                    />

                    <Form.Label className="share-editor-label">
                      Darken <span>{Math.round(backgroundDarkness * 100)}%</span>
                    </Form.Label>
                    <Form.Range
                      min={0}
                      max={0.7}
                      step={0.01}
                      value={backgroundDarkness}
                      onChange={(event) => setBackgroundDarkness(Number(event.target.value))}
                    />

                    <Form.Label className="share-editor-label">
                      Vignette <span>{Math.round(vignetteStrength * 100)}%</span>
                    </Form.Label>
                    <Form.Range
                      min={0}
                      max={1}
                      step={0.01}
                      value={vignetteStrength}
                      onChange={(event) => setVignetteStrength(Number(event.target.value))}
                    />
                  </>}
                </section>
                <section>
                  <ShareImageFrameControls frame={imageFrame} onChange={setImageFrame} />
                </section>
                <section>
                  <div className="share-editor-control-title">Card glow</div>
                  <div className="share-editor-segmented" aria-label="Glow mode">
                    {[{ id: "off", label: "Off" }, { id: "rarity", label: "Rarity" }, { id: "custom", label: "Custom" }].map((mode) => (
                      <button type="button" key={mode.id} aria-pressed={glow.mode === mode.id} className={glow.mode === mode.id ? "is-selected" : ""} onClick={() => setGlow((previous) => ({ ...previous, mode: mode.id }))}>{mode.label}</button>
                    ))}
                  </div>
                  {glow.mode === "rarity" && <div className="share-editor-rarity-key" aria-label="Rarity glow colors">
                    {RARITIES.map((rarity) => <span key={rarity.id} title={rarity.label} style={{ background: rarity.color }} />)}
                  </div>}
                  {glow.mode === "custom" && <ShareColorControl className="mt-2" aria-label="Shared glow color" value={glow.color} onChange={(event) => setGlow((previous) => ({ ...previous, color: event.target.value }))} />}
                  <Form.Label className="share-editor-label mt-3">Intensity <span>{Math.round(glow.strength * 100)}%</span></Form.Label>
                  <Form.Range aria-label="Glow intensity" min={0} max={1} step={0.01} value={glow.strength} onChange={(event) => setGlow((previous) => ({ ...previous, strength: Number(event.target.value) }))} />
                  <Form.Label className="share-editor-label">Softness <span>{glow.radius}px</span></Form.Label>
                  <Form.Range aria-label="Glow softness" min={6} max={52} step={1} value={glow.radius} onChange={(event) => setGlow((previous) => ({ ...previous, radius: Number(event.target.value) }))} />
                </section>
                <section>
                  <ShareShadowControls title="Card shadow" idPrefix="share-editor-shadow" shadow={cardShadow} defaults={DEFAULT_CARD_SHADOW} onChange={setCardShadow} directional={cardLighting.enabled} />
                  {cardShadow.enabled && <ShareCardLightControls lighting={cardLighting} onChange={setCardLighting}
                    lights={cardLights} onLightsChange={setCardLights} selectedLight={selectedLight} onSelectLight={setSelectedLight} />}
                </section>
                <section>
                  <div className="share-editor-control-title">Text &amp; labels</div>
                  <Form.Check
                    type="switch"
                    id="share-editor-title-toggle"
                    label="Show Card Title"
                    checked={showTitle}
                    onChange={(event) => setShowTitle(event.target.checked)}
                  />
                  <Form.Check
                    type="switch"
                    id="share-editor-brand-toggle"
                    label="Show HearthCards"
                    checked={showBrand}
                    onChange={(event) => setShowBrand(event.target.checked)}
                  />
                </section>
              </Tab>
              <Tab eventKey="graphics" title="Graphics" className="share-editor-settings-panel is-graphics">
                <ShareGraphicsPanel onAddTooltip={addTooltip} onAddDecoration={addDecoration} onAddText={addTextElement}
                  onImageUpload={handleImageUpload} imageInputRef={imageUploadInputRef} uploadsLoading={uploadsLoading}
                  tooltipLoading={tooltipLoading} isExporting={isExporting} />
              </Tab>
              <Tab eventKey="edit" title="Edit" className="share-editor-settings-panel is-edit">
                <section>
                  <div className="share-editor-control-title">Selected element</div>
                  <Form.Select
                    aria-label="Selected element"
                    size="sm"
                    className="mb-3"
                    value={selectedId || ""}
                    onChange={(event) => {
                      selectElement(event.target.value);
                    }}
                  >
                    <option value="" disabled>Select an element</option>
                    {elements.map((element) => (
                      <option value={element.id} key={element.id}>
                        {getElementDisplayLabel(element)}
                      </option>
                    ))}
                  </Form.Select>

                  {selectedElement && (
                    <Button variant="outline-danger" size="sm" className="w-100 mb-3" disabled={isExporting} onClick={removeSelectedElement}>
                      Delete selected {selectedElement.graphic === "tooltip" ? "tooltip" : selectedElement.decoration ? "decoration" : selectedElement.kind === "image" ? "image" : selectedElement.kind === "card" ? "card" : "text"}
                    </Button>
                  )}

                  {selectedElement?.decoration && <>
                    <Form.Label htmlFor="share-editor-decoration-style" className="share-editor-label">Style</Form.Label>
                    <Form.Select id="share-editor-decoration-style" size="sm" className="mb-3" value={selectedElement.decoration} onChange={(event) => {
                      const preset = DECORATIONS.find((item) => item.id === event.target.value);
                      updateElement(selectedElement.id, { decoration: preset.id, label: preset.label, height: selectedElement.width / preset.aspect });
                    }}>
                      {DECORATIONS.filter((preset) => preset.type === (selectedElement.decoration.endsWith("-pin") ? "pin" : "tape")).map((preset) => <option key={preset.id} value={preset.id}>{preset.label}</option>)}
                    </Form.Select>
                    <Form.Label className="share-editor-label">{selectedElement.decoration.endsWith("-pin") ? "Depth" : "Opacity"} <span>{Math.round((selectedElement.decoration.endsWith("-pin") ? selectedElement.depth : selectedElement.opacity) * 100)}%</span></Form.Label>
                    <Form.Range aria-label="Selected decoration depth or opacity" min={selectedElement.decoration.endsWith("-pin") ? 0.3 : 0.1} max={selectedElement.decoration.endsWith("-pin") ? 1.3 : 1} step={0.01} value={selectedElement.decoration.endsWith("-pin") ? selectedElement.depth : selectedElement.opacity} onChange={(event) => updateElement(selectedElement.id, selectedElement.decoration.endsWith("-pin") ? { depth: Number(event.target.value) } : { opacity: Number(event.target.value) })} />
                    <Button variant="outline-primary" size="sm" className="w-100 mb-3" onClick={duplicateGraphic}>Duplicate decoration</Button>
                  </>}

                  {selectedElement?.graphic === "tooltip" && <>
                    <Form.Label htmlFor="share-editor-tooltip-title" className="share-editor-label">Title</Form.Label>
                    <Form.Control id="share-editor-tooltip-title" size="sm" className="mb-3" value={selectedElement.title} maxLength={160}
                      onChange={(event) => updateElement(selectedElement.id, { title: event.target.value })} />
                    <Form.Label htmlFor="share-editor-tooltip-text" className="share-editor-label">Text</Form.Label>
                    <Form.Control id="share-editor-tooltip-text" as="textarea" rows={4} className="mb-3" value={selectedElement.text} maxLength={3000}
                      onChange={(event) => updateElement(selectedElement.id, { text: event.target.value })} />
                    <Form.Label className="share-editor-label">Text width<span>{selectedElement.layoutWidth}</span></Form.Label>
                    <Form.Range aria-label="Tooltip text wrapping width" min={180} max={520} step={5} value={selectedElement.layoutWidth}
                      onChange={(event) => updateElement(selectedElement.id, { layoutWidth: Number(event.target.value) })} />
                    <Button variant="outline-primary" size="sm" className="w-100 mb-3" onClick={duplicateGraphic}>Duplicate tooltip</Button>
                  </>}

                  {selectedElement?.kind === "card" && (
                    <>
                      <Form.Label htmlFor="share-editor-card-glow" className="share-editor-label">This card’s glow</Form.Label>
                      <Form.Select id="share-editor-card-glow" size="sm" className="mb-2" value={selectedElement.glowMode || "inherit"} onChange={(event) => updateElement(selectedElement.id, { glowMode: event.target.value })}>
                        <option value="inherit">Use shared setting</option><option value="off">Off</option><option value="rarity">Rarity color</option><option value="custom">Custom color</option>
                      </Form.Select>
                      {selectedElement.glowMode === "custom" && <ShareColorControl key={selectedElement.id} className="mb-3" aria-label="Selected card glow color" value={selectedElement.glowColor || "#80caff"} onChange={(event) => updateElement(selectedElement.id, { glowColor: event.target.value })} />}
                      <Form.Label htmlFor="share-editor-card-rarity" className="share-editor-label">Rarity color</Form.Label>
                      <Form.Select id="share-editor-card-rarity" size="sm" className="mb-3" value={selectedElement.rarityOverride || "auto"} onChange={(event) => updateElement(selectedElement.id, { rarityOverride: event.target.value })}>
                        <option value="auto">Automatic · {RARITIES.find((rarity) => rarity.id === selectedElement.rarity)?.label || "Not supplied"}</option>
                        {RARITIES.map((rarity) => <option key={rarity.id} value={rarity.id}>{rarity.label}</option>)}
                      </Form.Select>
                      <Form.Label className="share-editor-label">
                        Rotation <span>{Math.round(selectedElement.rotation)}°</span>
                      </Form.Label>
                      <Form.Range
                        aria-label="Selected card rotation"
                        min={-180}
                        max={180}
                        step={1}
                        value={selectedElement.rotation}
                        onChange={(event) =>
                          updateElement(selectedElement.id, {
                            rotation: Number(event.target.value),
                          })
                        }
                      />

                      <Form.Label className="share-editor-label">
                        Size <span>{Math.round(selectedElement.scale / (selectedElement.nativeWidthRatio || 1) * 100)}%</span>
                      </Form.Label>
                      <Form.Range
                        aria-label="Selected card size"
                        min={Math.min(0.05, selectedElement.scale / (selectedElement.nativeWidthRatio || 1))}
                        max={Math.max(2.5, selectedElement.scale / (selectedElement.nativeWidthRatio || 1))}
                        step={0.01}
                        value={selectedElement.scale / (selectedElement.nativeWidthRatio || 1)}
                        onChange={(event) =>
                          updateElement(selectedElement.id, {
                            scale: Number(event.target.value) * (selectedElement.nativeWidthRatio || 1),
                          })
                        }
                      />
                    </>
                  )}

                  {selectedElement?.kind === "image" && <>
                    <Form.Label className="share-editor-label">Rotation <span>{Math.round(selectedElement.rotation)}°</span></Form.Label>
                    <Form.Range aria-label="Selected image rotation" min={-180} max={180} step={1} value={selectedElement.rotation} onChange={(event) => updateElement(selectedElement.id, { rotation: Number(event.target.value) })} />
                    <Form.Label className="share-editor-label">Size <span>{Math.round(selectedElement.width / selectedElement.baseWidth * 100)}%</span></Form.Label>
                    <Form.Range aria-label="Selected image size" min={0.1} max={Math.max(4, selectedElement.width / selectedElement.baseWidth)} step={0.01} value={selectedElement.width / selectedElement.baseWidth} onChange={(event) => {
                      const width = selectedElement.baseWidth * Number(event.target.value);
                      updateElement(selectedElement.id, { width, height: width * selectedElement.height / selectedElement.width });
                    }} />
                  </>}

                  {selectedElement?.kind === "text" && (
                    <>
                      <Form.Label className="share-editor-label">Text content</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        className="mb-3"
                        value={selectedElement.text}
                        onChange={(event) =>
                          updateElement(selectedElement.id, {
                            text: event.target.value,
                          })
                        }
                      />

                      <Form.Label className="share-editor-label">
                        Rotation <span>{Math.round(selectedElement.rotation)}°</span>
                      </Form.Label>
                      <Form.Range
                        min={-180}
                        max={180}
                        step={1}
                        value={selectedElement.rotation}
                        onChange={(event) =>
                          updateElement(selectedElement.id, {
                            rotation: Number(event.target.value),
                          })
                        }
                      />

                      <Form.Label className="share-editor-label">
                        Size <span>{Math.round(selectedElement.fontSize)}px</span>
                      </Form.Label>
                      <Form.Range
                        min={Math.min(8, selectedElement.fontSize)}
                        max={Math.max(180, selectedElement.fontSize)}
                        step={1}
                        value={selectedElement.fontSize}
                        onChange={(event) =>
                          updateElement(selectedElement.id, {
                            fontSize: Number(event.target.value),
                          })
                        }
                      />

                      <Form.Label className="share-editor-label">Text color</Form.Label>
                      <ShareColorControl key={selectedElement.id} value={selectedElement.color || "#ffffff"}
                        onChange={(event) => updateElement(selectedElement.id, { color: event.target.value })} />
                    </>
                  )}

                  {(selectedElement?.kind === "image" || selectedElement?.kind === "text") && <details className="share-editor-placement">
                    <summary>{selectedElement.decoration ? "Extra shadow" : "Shadow"}</summary>
                    <ShareShadowControls key={selectedElement.id} idPrefix="share-editor-element-shadow" shadow={getElementShadow(selectedElement)} defaults={selectedElement.kind === "text" ? DEFAULT_TEXT_SHADOW : DEFAULT_IMAGE_SHADOW} onChange={(shadow) => updateElement(selectedElement.id, { shadow })} />
                  </details>}

                  {selectedElement && (
                    <div className="share-editor-element-actions">
                      <Button variant="outline-light" size="sm" onClick={() => updateElement(selectedElement.id, { x: scene.width / 2, y: scene.height / 2 })}>Center</Button>
                      <Button variant="outline-light" size="sm" onClick={() => moveSelectedLayer(false)}>To back</Button>
                      <Button variant="outline-light" size="sm" onClick={() => moveSelectedLayer(true)}>To front</Button>
                    </div>
                  )}
                  {selectedElement && (
                    <div className="share-editor-nudge-grid" aria-label="Nudge selected element">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label="Move selected element up"
                        onClick={() => nudgeSelected(0, -10)}
                      >
                        ↑
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label="Move selected element left"
                        onClick={() => nudgeSelected(-10, 0)}
                      >
                        ←
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label="Move selected element right"
                        onClick={() => nudgeSelected(10, 0)}
                      >
                        →
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        aria-label="Move selected element down"
                        onClick={() => nudgeSelected(0, 10)}
                      >
                        ↓
                      </Button>
                    </div>
                  )}
                </section>
              </Tab>
            </Tabs>
          </aside>
        </div>

        {exportError && <div className="alert alert-danger mt-3 mb-0">{exportError}</div>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Dropdown as={ButtonGroup} align="end" drop="up">
          <Button variant="primary" onClick={exportImage} disabled={isExporting || imagesLoading || uploadsLoading}>
            {isExporting ? "Rendering…" : imagesLoading ? "Preparing cards…" : uploadsLoading ? "Adding images…" : `Download ${selectedExportFormat.label}`}
          </Button>
          <Dropdown.Toggle split variant="primary" id="share-editor-export-format" className="share-editor-export-toggle" aria-label="Choose download format" disabled={isExporting || uploadsLoading}>
            <span aria-hidden="true">▾</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="share-editor-export-menu">
            {EXPORT_FORMATS.map((format) => (
              <Dropdown.Item as="button" type="button" key={format.id} active={exportFormat === format.id} onClick={() => setExportFormat(format.id)}>
                {format.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareCardEditor;
